// 최상위 branch를 **측정·계획 층에서 투명하게 통과**시키는 순수 로직(DOM/React 비의존).
//
// 배경: branch는 렌더에서 이미 "투명 논리 컨테이너"다 — outer가 `{width:'100%'}`, inner가
// `{display:'flex',flexDirection:'column'}`뿐이고 padding/border/장식/최소높이가 없다
// (RenderTemplate `case 'branch'`). 그래서 **한 branch의 활성 슬롯 자식을 여러 낱장에 나눠 그려도
// 출력이 동일**하다. 그런데 측정(pageMeasure)·계획(pagePlan)에는 branch 처리가 없어 최상위 branch가
// `data-composer-node-id` 하나의 offsetHeight로 재는 **원자 노드**였다. 결과: 그 안의 table/repeat이
// 분할 경계를 잃고, 페이지를 넘으면 `forcedPlacement: 'overflow'`로 낱장 밖으로 흘러넘쳤다.
//
// 해법은 계획 입력을 평탄화하고(아래 flattenBodyForMeasure) 낱장 slice에서 다시 감싸는 것
// (buildSliceBody)이다. **렌더 wrapper는 유지된다** — admin 편집 캔버스가 `data-composer-node-id`로
// branch 노드를 선택하므로 wrapper를 없애면 선택 불가 회귀가 난다.
//
// 하강은 **보수적**이다(아래 slotGainsFromDescent): 활성 슬롯의 자식에 table/repeat이 실제로 있을
// 때만 통과시킨다. 제목만 든 branch(si-prev-branch 등)를 굳이 쪼개면 제목이 페이지 끝에 남는
// orphan이 새로 생기므로, 문제가 되는 형태만 건드린다.

import type { BranchNode, ComposerNode } from '@safetyhub/shared';
import type { PagedItem } from './pagePlan';
import type { RenderData } from './types';
import { resolveActiveBranchSlot } from './branchSlot';
import type { BranchSlotContext } from './branchSlot';

/** branch 투명화 판정 입력 — 활성 슬롯 판정에 필요한 것 전부. */
export interface BranchPaginationContext extends BranchSlotContext {
  /**
   * 슬롯 condition 평가 대상(비편집 경로). **호스트는 반드시 렌더와 같은 값을 넘겨야 한다** —
   * 미지정이면 아래 `branchDescentSlot`이 하강을 포기한다(종전 원자 동작). 빈 데이터로 평가하지
   * 않는 이유는 `evaluateCondition`이 조건 미지정·미지원 문법에 **true**를 돌려주기 때문이다:
   * else 슬롯에 반복이 있으면 데이터 없이도 그 슬롯으로 하강해, 실렌더가 고른 슬롯과 갈라진다.
   */
  data?: RenderData;
}

/**
 * 이 branch를 하강시킬 활성 슬롯 인덱스, 아니면 -1.
 *
 * 미렌더(-1) 슬롯은 하강하지 않는다 — 그리지 않는 자식을 계획에 넣으면 계획이 가리키는 노드가
 * 화면에 없게 된다. 그 경우 종전대로 원자 노드로 측정된다(높이 0).
 *
 * 비편집인데 `data`가 없으면 **판정 자체를 포기**한다(안전한 쪽 = 종전 동작). 편집 캔버스는
 * 조건을 평가하지 않고 탭 선택(`activeBranchSlots`)만 쓰므로 data가 없어도 정확하다.
 */
export function branchDescentSlot(node: BranchNode, opts: BranchPaginationContext = {}): number {
  if (opts.editing !== true && opts.data === undefined) return -1;
  const idx = resolveActiveBranchSlot(node, opts.data ?? {}, opts);
  if (idx < 0) return -1;
  return slotGainsFromDescent(node.branches[idx].children, opts) ? idx : -1;
}

/**
 * 하강해서 **실제로 얻는 게 있는가** — 활성 슬롯의 **직속 자식**에 table/repeat이 있는가.
 *
 * 서브트리 전체를 뒤지지 않는 이유: group/stack 안에 든 repeat은 하강해도 그 group이 여전히 원자
 * 노드(offsetHeight 통짜)라 분할 경계가 살아나지 않는다 — 최상위 group에 든 repeat이 지금도 못
 * 쪼개지는 것과 같다. 얻는 것 없이 쪼개면 orphan(제목만 남는 페이지 끝)만 새로 만든다.
 * 다만 중첩 branch는 그 자체가 투명하므로 **통과해서** 안쪽 직속 자식을 본다.
 */
function slotGainsFromDescent(children: ComposerNode[], opts: BranchPaginationContext): boolean {
  return children.some((c) => {
    if (c.kind === 'table' || c.kind === 'repeat') return true;
    if (c.kind === 'branch') {
      // 여기까지 왔다면 branchDescentSlot의 data 게이트를 이미 통과했다(편집이거나 data 있음).
      const i = resolveActiveBranchSlot(c, opts.data ?? {}, opts);
      return i >= 0 && slotGainsFromDescent(c.branches[i].children, opts);
    }
    return false;
  });
}

/** 평탄화된 계획 입력 1건 — 측정할 노드 + (하강한 branch에서 물려받은) 강제 페이지 분리. */
export interface FlatBodyNode {
  node: ComposerNode;
  /**
   * 하강한 branch의 `pageBreak: 'before'`를 물려받았다는 표식(이 노드 **자신의** pageBreak는
   * 노드에 그대로 있으므로 여기 담지 않는다). branch는 계획에서 사라지므로 옮기지 않으면 유실된다.
   *
   * ⚠️ 물려받은 지시는 **높이를 차지하는 첫 자식**이 소진해야 한다 — 계획기는 높이 0 노드의
   * breakBefore를 무시하므로(무의미한 빈 분할 방지) 첫 자식이 0이면 그대로 사라진다. 높이는 측정
   * 뒤에야 알 수 있으니, 이 표식은 첫 자식에 붙이고 **이월은 measureBody가** 한다.
   */
  inheritedBreak?: true;
  /**
   * 이 노드를 낸 **하강 branch의 id**(최상위 노드면 undefined — 중첩이면 가장 바깥 branch).
   *
   * 물려받은 지시는 이 구간 안에서만 유효하다. 구간 표시가 없으면 측정 쪽 러닝 플래그가 branch
   * 자식이 전부 높이 0일 때(예: 반복이 0항목이라 미렌더) **구간 밖 다음 노드까지 흘러가** 무관한
   * 노드가 페이지 분리를 받는다.
   */
  span?: string;
}

/**
 * body를 **계획 입력 순서**로 평탄화한다 — 하강 대상 branch는 자기 대신 활성 슬롯 자식들을 낸다
 * (중첩 branch는 재귀). 하강 대상이 아닌 branch는 종전대로 자기 자신 1건이다(회귀 0).
 */
export function flattenBodyForMeasure(
  body: ComposerNode[],
  opts: BranchPaginationContext = {},
): FlatBodyNode[] {
  const out: FlatBodyNode[] = [];
  const walk = (nodes: ComposerNode[], inherited: boolean, span: string | undefined): void => {
    // 물려받은 pageBreak는 **첫 노드 하나**에만 붙인다(높이 0이면 measureBody가 다음으로 이월하되,
    // span 밖으로는 못 나간다).
    let pending = inherited;
    for (const n of nodes) {
      if (n.kind === 'branch') {
        const slot = branchDescentSlot(n, opts);
        if (slot >= 0) {
          // 사라지는 branch 자신의 pageBreak도 함께 물려준다. span은 **가장 바깥** 하강 branch가
          // 소유한다(중첩이어도 구간은 하나 — 이월이 그 밖으로 새지만 않으면 된다).
          walk(n.branches[slot].children, pending || n.pageBreak === 'before', span ?? n.id);
          pending = false;
          continue;
        }
      }
      const flat: FlatBodyNode = { node: n };
      if (pending) flat.inheritedBreak = true;
      if (span !== undefined) flat.span = span;
      out.push(flat);
      pending = false;
    }
  };
  walk(body, false, undefined);
  return out;
}

/**
 * 통짜(측정 전 초기/클립 리셋) 계획의 nodeId 목록 — **하강 결과와 같은 id 집합**이어야 한다.
 * 어긋나면 첫 계획에 branch 자식이 없어 측정(자식 단위)과 계획(branch 단위)이 갈린다.
 */
export function flattenedBodyIds(
  body: ComposerNode[],
  opts: BranchPaginationContext = {},
): string[] {
  return flattenBodyForMeasure(body, opts).map((f) => f.node.id);
}

/** 하강한 자식의 소유 branch 사슬 1칸(참조 동일성으로 사슬을 비교하므로 branch당 1개만 만든다). */
interface BranchOwner {
  branch: BranchNode;
  slotIndex: number;
  parent: BranchOwner | null;
}

interface Ownership {
  /** id → 노드. body 최상위 + 하강한 branch의 활성 슬롯 자식(재귀) 전부. */
  nodeById: Map<string, ComposerNode>;
  /** 하강한 자식 id → 그 자식을 소유한 branch(사슬). */
  ownerOf: Map<string, BranchOwner>;
}

function buildOwnership(body: ComposerNode[], opts: BranchPaginationContext): Ownership {
  const nodeById = new Map<string, ComposerNode>();
  const ownerOf = new Map<string, BranchOwner>();
  const walk = (nodes: ComposerNode[], parent: BranchOwner | null): void => {
    for (const n of nodes) {
      nodeById.set(n.id, n);
      if (parent) ownerOf.set(n.id, parent);
      if (n.kind === 'branch') {
        const slot = branchDescentSlot(n, opts);
        if (slot >= 0) walk(n.branches[slot].children, { branch: n, slotIndex: slot, parent });
      }
    }
  };
  walk(body, null);
  return { nodeById, ownerOf };
}

/** 소유 사슬을 바깥→안쪽 순으로. 소유 branch가 없으면 빈 배열(최상위 노드). */
function ownerChain(ownerOf: Map<string, BranchOwner>, id: string): BranchOwner[] {
  const chain: BranchOwner[] = [];
  let cur = ownerOf.get(id) ?? null;
  while (cur) {
    chain.push(cur);
    cur = cur.parent;
  }
  return chain.reverse();
}

/** 이 낱장 몫의 자식들로 branch 노드를 얕게 복제 — **활성 슬롯의 children만** 교체한다. */
function wrapInBranch(owner: BranchOwner, kids: ComposerNode[]): BranchNode {
  return {
    ...owner.branch,
    // 다른 슬롯은 원본 그대로 둔다 — 렌더가 같은 판정으로 같은 슬롯을 다시 고르므로 일치한다
    // (교체한 활성 슬롯만 이 낱장 몫이다).
    branches: owner.branch.branches.map((slot, i) =>
      i === owner.slotIndex ? { ...slot, children: kids } : slot,
    ),
  };
}

/**
 * 낱장의 최상위 PagedItem들 → RenderTemplate에 넘길 body slice.
 *
 * 하강한 branch 자식은 **연속된 같은 branch 자식끼리 묶어** 그 branch 노드로 다시 감싼다(중첩은
 * 안쪽부터). 감싸지 않으면 렌더에 branch wrapper가 사라져 admin 편집 캔버스의 노드 선택
 * (`data-composer-node-id`)이 깨진다. 하강하지 않은 노드는 종전대로 그 노드 그대로다(회귀 0).
 *
 * body에 없는 nodeId(내용 변경 직후의 stale 계획 등)는 조용히 버린다 — 종전 호스트 구현과 동일.
 */
export function buildSliceBody(
  topLevel: PagedItem[],
  body: ComposerNode[],
  opts: BranchPaginationContext = {},
): ComposerNode[] {
  const { nodeById, ownerOf } = buildOwnership(body, opts);
  const out: ComposerNode[] = [];
  // 열려 있는 branch 사슬 — 사슬이 끊기는 지점에서 안쪽부터 닫아 부모 kids로 접어 넣는다.
  const open: { owner: BranchOwner; kids: ComposerNode[] }[] = [];
  const closeTo = (depth: number): void => {
    while (open.length > depth) {
      const top = open[open.length - 1];
      open.pop();
      const wrapped = wrapInBranch(top.owner, top.kids);
      if (open.length > 0) open[open.length - 1].kids.push(wrapped);
      else out.push(wrapped);
    }
  };
  for (const it of topLevel) {
    const node = nodeById.get(it.nodeId);
    if (!node) continue;
    const chain = ownerChain(ownerOf, it.nodeId);
    let common = 0;
    while (common < chain.length && common < open.length && open[common].owner === chain[common]) {
      common += 1;
    }
    closeTo(common);
    for (let d = common; d < chain.length; d += 1) open.push({ owner: chain[d], kids: [] });
    if (open.length > 0) open[open.length - 1].kids.push(node);
    else out.push(node);
  }
  closeTo(0);
  return out;
}
